import React, { useEffect, useMemo, useState } from "react";
import { Container, Card, CardBody, Table, Badge, Spinner } from "reactstrap";
import { Link } from "react-router-dom";
import { useAuthUser } from "../../../hooks/AuthProvider";
import { getListingsByOwner, getListingInquiriesByPm } from "../../../helpers/firestoreService";
import PageHero from "../../../components/Common/PageHero";

/** Every rental listing the manager created, with an unread-inquiry badge. */
const ListingsPage = () => {
  document.title = "Listings | Support Portal";

  const user = useAuthUser();
  const [listings, setListings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [l, i] = await Promise.all([
          getListingsByOwner(user.uid),
          getListingInquiriesByPm(user.uid),
        ]);
        if (cancelled) return;
        setListings(l);
        setInquiries(i);
      } catch (e) {
        console.error("Load listings error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const statsByListing = useMemo(() => {
    const map = new Map();
    inquiries.forEach((inq) => {
      const cur = map.get(inq.listingId) || { count: 0, unread: 0 };
      cur.count += 1;
      if (inq.unreadByPm) cur.unread += 1;
      map.set(inq.listingId, cur);
    });
    return map;
  }, [inquiries]);

  return (
    <div className="page-content">
      <Container fluid>
        <PageHero
          title="Listings"
          subtitle="Your published rental listings and any inquiries prospects have sent in."
        />

        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : listings.length === 0 ? (
          <Card>
            <CardBody className="text-center py-5">
              <i className="bx bx-building-house text-muted" style={{ fontSize: "2.5rem" }} />
              <h5 className="text-muted mt-2">No listings yet</h5>
              <p className="text-muted mb-0">
                Create a rental application link from a property page to publish a listing.
              </p>
            </CardBody>
          </Card>
        ) : (
          <Card>
            <CardBody>
              <div className="table-responsive">
                <Table className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Address</th>
                      <th>Rent</th>
                      <th>Status</th>
                      <th>Inquiries</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map((l) => {
                      const stats = statsByListing.get(l.id);
                      return (
                        <tr key={l.id}>
                          <td>{l.address || "—"}</td>
                          <td>{l.monthlyRent ? `$${l.monthlyRent}/mo` : "—"}</td>
                          <td>
                            <Badge color={l.active ? "success" : "secondary"} className="fw-normal">
                              {l.active ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td>
                            {stats?.count ? (
                              <>
                                {stats.count} inquir{stats.count === 1 ? "y" : "ies"}
                                {stats.unread > 0 && (
                                  <Badge color="danger" pill className="ms-2">{stats.unread} new</Badge>
                                )}
                              </>
                            ) : (
                              <span className="text-muted">None yet</span>
                            )}
                          </td>
                          <td className="text-end">
                            <Link to={`/listings/${l.id}`} className="btn btn-sm btn-outline-primary">
                              Open
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default ListingsPage;
